import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Sun, Droplets, CheckCircle, Camera } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function SkinAssessmentGuide() {
  const navigate = useNavigate();

  const skinTypes = [
    { type: "Oily", signs: ["Shiny T-zone", "Enlarged pores", "Prone to breakouts", "Makeup slides off"], care: "Use oil-free products, clay masks, niacinamide" },
    { type: "Dry", signs: ["Tight feeling", "Flaky patches", "Dull appearance", "Fine lines visible"], care: "Rich moisturizers, hyaluronic acid, avoid harsh cleansers" },
    { type: "Combination", signs: ["Oily T-zone", "Dry cheeks", "Occasional breakouts", "Varying pore size"], care: "Zone-specific products, balance is key" },
    { type: "Normal", signs: ["Even texture", "Few imperfections", "Not too oily or dry", "Small pores"], care: "Maintain with gentle products, focus on prevention" },
    { type: "Sensitive", signs: ["Redness", "Reactions to products", "Burning or stinging", "Easily irritated"], care: "Fragrance-free, minimal ingredients, patch test everything" }
  ];

  const concerns = [
    { concern: "Fine Lines & Wrinkles", solutions: ["Retinol", "Peptides", "Vitamin C", "SPF daily"] },
    { concern: "Dark Spots & Hyperpigmentation", solutions: ["Vitamin C", "Niacinamide", "Alpha Arbutin", "SPF daily"] },
    { concern: "Dullness", solutions: ["Exfoliation", "Vitamin C", "Hydration", "AHAs"] },
    { concern: "Loss of Firmness", solutions: ["Retinol", "Peptides", "Facial massage", "Collagen support"] },
    { concern: "Dehydration", solutions: ["Hyaluronic acid", "Ceramides", "Gentle cleanser", "Humidifier"] },
    { concern: "Uneven Texture", solutions: ["BHAs/AHAs", "Retinol", "Regular exfoliation", "Hydration"] }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-pink-100 text-pink-700 rounded-full text-sm font-semibold mb-4">
            Week 3 • Day 1 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Skin Assessment
          </h1>
          <p className="text-xl text-secondary-600">Know Your Skin to Transform It</p>
        </div>

        {/* Today's Practice */}
        <section className="bg-gradient-to-r from-pink-500 to-rose-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Search className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Today's Practice</h3>
              <p className="text-pink-100 text-lg">
                In natural light (near a window), examine your bare face closely with a mirror. Take a makeup-free selfie as your baseline to compare to at the end of this week.
              </p>
            </div>
          </div>
        </section>

        {/* Skin Types */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Identify Your Skin Type</h2>
          <div className="space-y-4">
            {skinTypes.map((skin, index) => (
              <div key={index} className="bg-pink-50 rounded-xl p-5">
                <h3 className="text-lg font-bold text-pink-700 mb-3">{skin.type} Skin</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-semibold text-secondary-500 mb-2">Signs:</p>
                    <ul className="space-y-1">
                      {skin.signs.map((sign, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-secondary-700">
                          <CheckCircle className="w-4 h-4 text-pink-500 flex-shrink-0" />
                          {sign}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-sm text-secondary-700"><strong>Care Tips:</strong> {skin.care}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Common Concerns */}
        <section className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl p-8 border-2 border-pink-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Common Concerns in Your 40s</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {concerns.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4">
                <h3 className="font-bold text-pink-700 mb-2">{item.concern}</h3>
                <div className="flex flex-wrap gap-2">
                  {item.solutions.map((sol, i) => (
                    <span key={i} className="px-2 py-1 bg-pink-100 text-pink-700 rounded-full text-xs">
                      {sol}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Baseline Photo */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <Camera className="w-8 h-8 text-pink-500" />
            <h2 className="text-2xl font-serif font-bold text-secondary-900">Take Your Baseline Photo</h2>
          </div>
          <p className="text-secondary-700 mb-4">For best results:</p>
          <ul className="space-y-2">
            {[
              "Use natural lighting (near a window, not direct sunlight)",
              "No makeup - completely bare face",
              "Pull hair back from face",
              "Take photos from front and both profiles",
              "Keep a neutral expression",
              "Save in a dedicated folder to compare later"
            ].map((tip, i) => (
              <li key={i} className="flex items-center gap-2 text-secondary-700">
                <CheckCircle className="w-4 h-4 text-pink-500 flex-shrink-0" />
                {tip}
              </li>
            ))}
          </ul>
        </section>
      </main>
    </div>
  );
}

export default SkinAssessmentGuide;
